/**
 * Phase 5A — Admin V5 Payments Panel.
 * Reads /api/admin/payments — shows KPI strip + recent payments table.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCcw, DollarSign, TrendingUp, AlertCircle, CheckCircle2, ShieldOff, Receipt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const money = (cents, ccy = 'AUD') => `${ccy} ${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

const Kpi = ({ label, value, sub, tone = 'text-gray-900', testid }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold">{label}</div>
    <div className={`text-xl font-bold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const PaymentStatusBadge = ({ s }) => {
  const map = {
    paid: { bg: 'bg-emerald-100 text-emerald-800', label: 'Paid' },
    pending: { bg: 'bg-amber-100 text-amber-800', label: 'Pending' },
    failed: { bg: 'bg-red-100 text-red-800', label: 'Failed' },
    refunded: { bg: 'bg-gray-200 text-gray-800', label: 'Refunded' },
  };
  const v = map[s] || { bg: 'bg-gray-100 text-gray-700', label: s || '—' };
  return <Badge className={v.bg}>{v.label}</Badge>;
};

const AdminPaymentsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [payments, setPayments] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await ax.get('/api/admin/payments');
      setKpis(r.data?.kpis);
      setPayments(r.data?.payments || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load payments');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markRefunded = async (pid) => {
    const reason = window.prompt('Refund reason (optional):');
    if (reason === null) return;
    try {
      await ax.post(`/api/admin/payments/${pid}/mark-refunded`, { reason });
      toast.success('Marked refunded');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refund failed'); }
  };

  const exportCsv = () => {
    if (!payments.length) return;
    const rows = [
      ['Created', 'Booking', 'Student', 'Tutor', 'Amount AUD', 'Charged USD', 'FX', 'Status', 'Tutor share AUD', 'Winhouse share AUD', 'Reference'].join(','),
      ...payments.map((p) => [
        p.created_at, p.booking_id, p.student_email, p.tutor_email,
        ((p.amount_aud_cents || p.amount_cents || 0) / 100).toFixed(2),
        ((p.charged_amount_usd_cents || 0) / 100).toFixed(2),
        p.fx_rate_used || '',
        p.status,
        ((p.tutor_share_cents || 0) / 100).toFixed(2),
        ((p.winhouse_share_cents || 0) / 100).toFixed(2),
        p.payment_reference,
      ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `winhouse_payments_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const editFx = async () => {
    const cur = kpis?.fx_rate_aud_to_usd ?? 0.65;
    const v = window.prompt(`Current AUD→USD rate: ${cur}\nEnter new rate (e.g. 0.65):`, String(cur));
    if (!v) return;
    const rate = Number(v);
    if (!(rate > 0 && rate < 10)) { toast.error('Rate must be between 0 and 10'); return; }
    try {
      await ax.put('/api/admin/settings/fx', { rate });
      toast.success(`FX rate updated to ${rate}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'FX update failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  const ccy = kpis?.currency || 'AUD';

  return (
    <div className="space-y-6" data-testid="admin-payments-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-payments-kpis">
        <Kpi label="Total Revenue" value={money(kpis?.total_revenue_cents, ccy)} tone="text-emerald-700" testid="kpi-total-revenue" />
        <Kpi label="Paid Bookings" value={kpis?.paid_bookings ?? 0} tone="text-emerald-700" testid="kpi-paid-bookings" />
        <Kpi label="Pending Payments" value={kpis?.pending_payments ?? 0} tone="text-amber-700" testid="kpi-pending-payments" />
        <Kpi label="Failed Payments" value={kpis?.failed_payments ?? 0} tone="text-red-700" testid="kpi-failed-payments" />
        <Kpi label="Tutor Earnings Pending" value={money(kpis?.tutor_earnings_pending_cents, ccy)} testid="kpi-tutor-pending" />
        <Kpi label="Platform Revenue" value={money(kpis?.platform_revenue_cents, ccy)} tone="text-emerald-700" testid="kpi-platform-revenue" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Recent payments</CardTitle>
            <p className="text-sm text-gray-500 mt-1">Latest 500 transactions across the platform.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={editFx} data-testid="edit-fx-rate" className="text-[10px]">
              FX: AUD → USD {kpis?.fx_rate_aud_to_usd ? kpis.fx_rate_aud_to_usd.toFixed(4) : '0.6500'}
            </Button>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={!payments.length} data-testid="export-payments-csv">
              Export CSV
            </Button>
            <Button size="sm" variant="outline" onClick={fetchData} disabled={refreshing} data-testid="refresh-payments">
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center text-sm text-gray-400 italic py-12" data-testid="payments-empty">No payments yet — first booking payment will appear here.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-gray-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">When</th>
                    <th className="text-left py-2 pr-3">Type</th>
                    <th className="text-left py-2 pr-3">Student / Donor</th>
                    <th className="text-left py-2 pr-3">Tutor</th>
                    <th className="text-left py-2 pr-3">AUD Source</th>
                    <th className="text-left py-2 pr-3">Selected</th>
                    <th className="text-left py-2 pr-3">Displayed</th>
                    <th className="text-left py-2 pr-3">USD Checkout</th>
                    <th className="text-left py-2 pr-3">FX</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Tutor share</th>
                    <th className="text-left py-2 pr-3">Platform</th>
                    <th className="text-left py-2 pr-3">Payout</th>
                    <th className="text-left py-2 pr-3">Reference</th>
                    <th className="text-left py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    // Phase 5D — payments now carry source_amount_aud + selected_payment_currency + displayed_amount + charged_amount_usd.
                    // Legacy rows fall back to amount_aud_cents/charged_amount_usd_cents.
                    const audCents = p.amount_aud_cents != null ? p.amount_aud_cents :
                      (p.source_amount_aud != null ? Math.round(p.source_amount_aud * 100) :
                       (p.amount_cents != null ? p.amount_cents :
                        (p.amount != null ? Math.round(p.amount * 100) : 0)));
                    const usdCents = p.charged_amount_usd_cents != null ? p.charged_amount_usd_cents :
                      (p.charged_amount_usd != null ? Math.round(p.charged_amount_usd * 100) : null);
                    const sel = (p.selected_payment_currency || p.currency || 'AUD').toUpperCase();
                    const disp = p.displayed_amount != null ? p.displayed_amount :
                      (sel === 'AUD' ? (audCents / 100) : (usdCents != null ? (usdCents / 100) : (audCents / 100)));
                    const pType = (p.payment_type || (p.tutor_email ? 'booking' : 'manual')).replace('_', ' ');
                    return (
                    <tr key={p.id} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmt(p.created_at)}</td>
                      <td className="py-2 pr-3"><Badge className="bg-gray-100 text-gray-800 capitalize text-[10px]">{pType}</Badge></td>
                      <td className="py-2 pr-3 break-all">{p.student_email}</td>
                      <td className="py-2 pr-3 break-all">{p.tutor_email || '—'}</td>
                      <td className="py-2 pr-3 font-bold" data-testid={`payment-aud-${p.id}`}>{money(audCents, 'AUD')}</td>
                      <td className="py-2 pr-3"><Badge className="bg-blue-50 text-blue-800 text-[10px]">{sel}</Badge></td>
                      <td className="py-2 pr-3">{sel} {Number(disp || 0).toFixed(2)}</td>
                      <td className="py-2 pr-3 text-emerald-700 font-medium" data-testid={`payment-usd-${p.id}`}>{usdCents != null ? money(usdCents, 'USD') : '—'}</td>
                      <td className="py-2 pr-3 text-[10px]">{p.fx_rate_used ? p.fx_rate_used.toFixed(4) : '—'}</td>
                      <td className="py-2 pr-3"><PaymentStatusBadge s={p.status} /></td>
                      <td className="py-2 pr-3">{money(p.tutor_share_cents != null ? p.tutor_share_cents : Math.round((p.tutor_amount || 0) * 100), 'AUD')}</td>
                      <td className="py-2 pr-3">{money(p.winhouse_share_cents != null ? p.winhouse_share_cents : Math.round((p.platform_amount || 0) * 100), 'AUD')}</td>
                      <td className="py-2 pr-3">
                        <Badge className={p.payout_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-800'}>{p.payout_status || 'pending'}</Badge>
                      </td>
                      <td className="py-2 pr-3 font-mono text-[10px] break-all">{p.payment_reference || p.paystack_reference || '—'}</td>
                      <td className="py-2 pr-3">
                        {p.status === 'paid' && (
                          <Button size="sm" variant="outline" onClick={() => markRefunded(p.id)} className="text-[10px] gap-1" data-testid={`payment-refund-${p.id}`}>
                            <ShieldOff className="h-3 w-3" /> Refund
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPaymentsPanel;
